import { gql, useLazyQuery } from '@apollo/client';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { useMessages } from 'hooks';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Account } from 'types/Account';
import { downloadURIPromise } from 'services/downloadURI';
import dayjs from 'dayjs';
import { DesktopDateTimePicker } from '@mui/x-date-pickers';
import ExportVideoIcon from './ExportVideoIcon';

const CAMERA_DOWNLOAD_FILE_URL = gql`
  query GetCameraDownloadFileURL(
    $isCloud: Boolean!
    $masterCompanyId: Int!
    $accountId: Int!
    $accountType: Int!
    $doorId: Int
    $email: String
    $isSIMCloud: Boolean
    $userName: String
    $camName: String!
    $initialDate: String!
    $timezone: Int
  ) {
    getCameraDownloadFileURL(
      isCloud: $isCloud
      masterCompanyId: $masterCompanyId
      accountId: $accountId
      accountType: $accountType
      doorId: $doorId
      email: $email
      isSIMCloud: $isSIMCloud
      userName: $userName
      camName: $camName
      initialDate: $initialDate
      timezone: $timezone
    ) {
      url
      token
    }
  }
`;

function ExportVideo({ accountSelected, camName }: { accountSelected: Account; camName: string }) {
  const { t } = useTranslation('cameras');
  const { enqueueError } = useMessages();
  const [open, setOpen] = React.useState(false);
  const [downloadingVideo, setDownloadingVideo] = React.useState(false);

  const [getUrl, { loading, error }] = useLazyQuery<{
    getCameraDownloadFileURL: { url: string; token: string };
  }>(CAMERA_DOWNLOAD_FILE_URL, {
    fetchPolicy: 'no-cache',
    variables: {
      ...accountSelected,
      accountId: accountSelected.id,
      camName,
    },
  });

  const handleGetImage = React.useCallback(
    async (initialDate: string) => {
      const retorno = await getUrl({ variables: { initialDate } });
      if (retorno.data?.getCameraDownloadFileURL) {
        setOpen(false);
        setDownloadingVideo(true);
        try {
          await downloadURIPromise(
            retorno.data?.getCameraDownloadFileURL.url,
            `${camName}-${dayjs(initialDate || undefined).format('L LTS')}.mp4`,
            retorno.data?.getCameraDownloadFileURL.token,
          );
        } catch {
          enqueueError(t('error_exporting_image'));
        } finally {
          setDownloadingVideo(false);
        }
      }
    },
    [camName, enqueueError, getUrl, t],
  );

  React.useEffect(
    function alertOnError() {
      if (error) {
        enqueueError(t('error_exporting_image'));
      }
    },
    [enqueueError, error, t],
  );

  if (loading || downloadingVideo) {
    return (
      <IconButton size="small">
        <CircularProgress size={24} />
      </IconButton>
    );
  }

  return (
    <>
      <Tooltip title={t('export_video')} arrow>
        <IconButton color="inherit" size="small" onClick={() => setOpen(true)}>
          <ExportVideoIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <ExportVideoModal {...{ open, setOpen, handleGetImage }} />
    </>
  );
}

function ExportVideoModal({
  open,
  setOpen,
  handleGetImage,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleGetImage: (initialDate: string) => Promise<void>;
}) {
  const [initialDate, setInitialDate] = React.useState<string | null>(null);
  const { t } = useTranslation(['cameras', '_common']);

  return (
    <Dialog
      {...{ open }}
      onClose={() => setOpen(false)}
      aria-labelledby="create-modal-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="create-modal-title">{t('cameras:video_export')}</DialogTitle>

      <DialogContent>
        <DesktopDateTimePicker
          inputFormat="L LT"
          value={initialDate}
          // @ts-ignore
          maxDateTime={dayjs()}
          onChange={(newDate: Date | null) => {
            newDate &&
              // @ts-ignore
              !isNaN(newDate) &&
              setInitialDate(dayjs(newDate).toISOString());
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              inputProps={{
                ...params.inputProps,
                placeholder: `${dayjs().localeData().longDateFormat('L')} ${dayjs()
                  .localeData()
                  .longDateFormat('LT')}`,
              }}
            />
          )}
        />
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={() => setOpen(false)}>{t('_common:close')}</Button>
        <Button variant="contained" onClick={() => initialDate && handleGetImage(initialDate)}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ExportVideo;
